import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { TrendDataPoint } from '../../lib/financialsApi';
import { useI18n } from '../../contexts/I18nContext';

interface RevenueCostChartProps {
  /** Historical actual data points. */
  actuals: TrendDataPoint[];
  /** Projected forecast points (rendered dashed). */
  forecast?: TrendDataPoint[];
}

// The SVG is rendered at the real container width (not scaled via viewBox), so
// text keeps a fixed, readable size regardless of how wide the page is.
const MIN_WIDTH = 640;
const HEIGHT = 320;
const PADDING = { top: 16, right: 16, bottom: 36, left: 64 };
/** Approximate horizontal room one X label needs before we start skipping. */
const X_LABEL_SPACE = 56;

interface Series {
  color: string;
  labelKey: string;
  key: 'revenue' | 'expenses' | 'profit';
}

const SERIES: Series[] = [
  { color: '#2563eb', labelKey: 'financials.chart.revenue', key: 'revenue' },
  { color: '#dc2626', labelKey: 'financials.chart.costs', key: 'expenses' },
  { color: '#16a34a', labelKey: 'financials.chart.profit', key: 'profit' },
];

/** Width of a DOM element, tracked via ResizeObserver. */
function useContainerWidth(): { ref: React.RefObject<HTMLDivElement>; width: number } {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, width };
}

/**
 * Dependency-free SVG line chart of revenue vs costs (and profit) over time.
 * Forecast points are rendered with a dashed line to distinguish projections.
 * The chart stretches to the container's full width; below MIN_WIDTH it
 * scrolls horizontally instead of shrinking the fonts.
 */
export function RevenueCostChart({ actuals, forecast = [] }: RevenueCostChartProps) {
  const { t } = useI18n();
  const points = useMemo(() => [...actuals, ...forecast], [actuals, forecast]);
  const { ref, width: containerWidth } = useContainerWidth();
  const width = Math.max(MIN_WIDTH, containerWidth);

  const { scaleX, scaleY, yTicks, xLabelStep } = useMemo(() => {
    const values = points.flatMap((p) => [p.revenue, p.expenses, p.profit]);
    const max = Math.max(1, ...values);
    const min = Math.min(0, ...values);
    const innerW = width - PADDING.left - PADDING.right;
    const innerH = HEIGHT - PADDING.top - PADDING.bottom;
    const n = Math.max(1, points.length - 1);

    const sx = (i: number) => PADDING.left + (innerW * i) / n;
    const sy = (v: number) => PADDING.top + innerH - (innerH * (v - min)) / (max - min || 1);

    const ticks = Array.from({ length: 5 }, (_, i) => min + ((max - min) * i) / 4);
    // Skip every nth X label when they would overlap.
    const step = Math.max(1, Math.ceil((points.length * X_LABEL_SPACE) / Math.max(1, innerW)));
    return { scaleX: sx, scaleY: sy, yTicks: ticks, xLabelStep: step };
  }, [points, width]);

  if (points.length === 0) {
    return <p className="text-gray-500">{t('financials.chart.empty')}</p>;
  }

  const actualCount = actuals.length;

  function pathFor(key: Series['key'], from: number, to: number): string {
    const slice = points.slice(from, to);
    return slice
      .map((p, idx) => {
        const i = from + idx;
        return `${idx === 0 ? 'M' : 'L'} ${scaleX(i).toFixed(1)} ${scaleY(p[key]).toFixed(1)}`;
      })
      .join(' ');
  }

  return (
    <div ref={ref} className="w-full overflow-x-auto">
      <svg width={width} height={HEIGHT} role="img" aria-label={t('financials.chart.ariaLabel')}>
        {/* Y grid + labels */}
        {yTicks.map((tick) => (
          <g key={tick}>
            <line
              x1={PADDING.left}
              x2={width - PADDING.right}
              y1={scaleY(tick)}
              y2={scaleY(tick)}
              stroke="#e5e7eb"
              strokeWidth={1}
            />
            <text
              x={PADDING.left - 8}
              y={scaleY(tick) + 4}
              textAnchor="end"
              fontSize={11}
              fill="#6b7280"
            >
              {Math.round(tick).toLocaleString('ro-RO')}
            </text>
          </g>
        ))}

        {/* X labels (period), thinned when dense */}
        {points.map((p, i) =>
          i % xLabelStep === 0 || i === points.length - 1 ? (
            <text
              key={`${p.period}-${i}`}
              x={scaleX(i)}
              y={HEIGHT - 12}
              textAnchor="middle"
              fontSize={11}
              fill={i >= actualCount ? '#9ca3af' : '#6b7280'}
            >
              {p.period}
            </text>
          ) : null,
        )}

        {/* Lines per series: solid actuals, dashed forecast */}
        {SERIES.map((s) => (
          <g key={s.key}>
            <path d={pathFor(s.key, 0, actualCount)} fill="none" stroke={s.color} strokeWidth={2} />
            {forecast.length > 0 && (
              <path
                d={pathFor(s.key, Math.max(0, actualCount - 1), points.length)}
                fill="none"
                stroke={s.color}
                strokeWidth={2}
                strokeDasharray="5 4"
                opacity={0.7}
              />
            )}
            {points.map((p, i) => (
              <circle
                key={`${s.key}-${i}`}
                cx={scaleX(i)}
                cy={scaleY(p[s.key])}
                r={2.5}
                fill={s.color}
              />
            ))}
          </g>
        ))}
      </svg>

      {/* Legend */}
      <div className="mt-2 flex flex-wrap gap-4 text-sm">
        {SERIES.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: s.color }}
            />
            <span className="text-gray-700">{t(s.labelKey)}</span>
          </span>
        ))}
        {forecast.length > 0 && (
          <span className="text-gray-500">{t('financials.chart.legend')}</span>
        )}
      </div>
    </div>
  );
}
